import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";

// Item réordonnable — passe editMode pour afficher la poignée
export const SortableItem = ({ id, editMode, children, className = "" }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 50 : "auto",
  };
  return (
    <div ref={setNodeRef} style={style} className={`relative ${className}`} data-testid={`sortable-${id}`}>
      {editMode && (
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="absolute top-1 right-1 z-10 p-1 bg-white/95 rounded shadow border border-slate-300 cursor-grab active:cursor-grabbing hover:bg-indigo-50"
          title="Glisser pour réorganiser"
          data-testid={`drag-handle-${id}`}
        >
          <GripVertical className="w-4 h-4 text-slate-500" />
        </button>
      )}
      {children}
    </div>
  );
};

// Wrapper qui prend une liste d'ids et applique le drag
export const SortableList = ({ items, onReorder, children, orientation = "vertical" }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.indexOf(active.id);
    const newIndex = items.indexOf(over.id);
    if (oldIndex >= 0 && newIndex >= 0) onReorder(arrayMove(items, oldIndex, newIndex));
  };
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
};

// Utilitaire : réordonne une liste d'objets selon l'ordre sauvegardé
export const applySavedOrder = (items, keyField, storageKey) => {
  try {
    const saved = JSON.parse(localStorage.getItem(storageKey) || "[]");
    if (!Array.isArray(saved) || saved.length === 0) return items;
    const map = new Map(items.map(i => [i[keyField], i]));
    const ordered = [];
    saved.forEach(k => { if (map.has(k)) { ordered.push(map.get(k)); map.delete(k); } });
    // Ajoute les nouveaux items (non encore dans l'ordre sauvegardé) à la fin
    map.forEach(v => ordered.push(v));
    return ordered;
  } catch { return items; }
};

export const saveOrder = (storageKey, keys) => {
  try { localStorage.setItem(storageKey, JSON.stringify(keys)); } catch { /* ignore */ }
};
